import React, { useState, useEffect } from "react";
import styles from "./create.module.css";
import { Helmet } from "react-helmet";
import { Stepper } from "react-form-stepper";
import { Col, Row, Form, Button, Spinner } from "react-bootstrap";
import Header from "../../Component/Header/Header";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  GET_CATEGORIES,
  GET_FORMS,
  GET_RESEARCHES_DETAIL,
  UPDATE_RESEARCH,
} from "../../APIs/BaseUrl";
import Notification from "../../Component/Notification/Notification";
import { useAuth } from "../../Context/AuthContext";

const RESEARCH_TYPES = [
  { label: "Online Study", value: "online study" },
  { label: "Group Study", value: "group study" },
  { label: "Detail Interview", value: "detail interview" },
  { label: "Central Location Test", value: "central location test" },
  { label: "Survey", value: "survey" },
  { label: "Diary Study", value: "diary study" },
];

const EditProject = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toggleNotification } = useAuth();
  const [step, setStep] = useState(0);

  // Form state
  const [form, setForm] = useState({
    research_name: "",
    type: "",
    description: "",
    is_age_limited: false,
    age_limit: { min_age: "", max_age: "" },
    duration: "",
    category: "",
    location: "",
    price: "",
    doc: "",
    max_applications: 0,
    status: true,
    is_form_applicable: false,
    form_questionair: "",
  });

  // Dropdown data
  const [categories, setCategories] = useState([]);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch categories and forms
  useEffect(() => {
    const token = localStorage.getItem("token");
    axios
      .get(`${GET_CATEGORIES}?limit=100&page=1`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => setCategories(data?.data?.docs || []));

    axios
      .get(`${GET_FORMS}?limit=100&page=1`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => setForms(data?.data?.docs || []));
  }, []);

  // Fetch research detail for edit
  useEffect(() => {
    const fetchDetail = async () => {
      const token = localStorage.getItem("token");
      try {
        const { data } = await axios.get(GET_RESEARCHES_DETAIL(id), {
          headers: { Authorization: `Bearer ${token}` },
        });
        const d = data?.data;
        setForm({
          research_name: d?.research_name || "",
          type: d?.type || "",
          description: d?.description || "",
          is_age_limited: d?.is_age_limited || false,
          age_limit: {
            min_age: d?.age_limit?.min_age || "",
            max_age: d?.age_limit?.max_age || "",
          },
          duration: d?.duration || "",
          category: d?.category?._id || d?.category || "",
          location: d?.location || "",
          price: d?.price || "",
          doc: d?.doc || "",
          max_applications: d?.max_applications || 0,
          status: typeof d?.status === "boolean" ? d.status : true,
          is_form_applicable: d?.is_form_applicable || false,
          form_questionair: d?.form_questionair?._id || d?.form_questionair || "",
        });
      } catch (err) {
        alert("Failed to fetch research details");
        navigate("/researches");
      }
    };
    if (id) fetchDetail();
  }, [id, navigate]);

  // Handlers
  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    if (
      name === "is_age_limited" ||
      name === "status" ||
      name === "is_form_applicable"
    ) {
      setForm({ ...form, [name]: checked });
    } else if (name === "min_age" || name === "max_age") {
      setForm({ ...form, age_limit: { ...form.age_limit, [name]: value } });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleNext = () => setStep((prev) => prev + 1);
  const handleBack = () => setStep((prev) => prev - 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(UPDATE_RESEARCH(id), form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      navigate("/researches");
    } catch (err) {
      alert("Failed to update research");
    }
    setLoading(false);
  };

  return (
    <React.Fragment>
      <div className="right-aside">
        <Helmet>Edit Project</Helmet>
        <Row>
          <Col md={!toggleNotification ? 12 : 8} sm={12}>
            <Header />
            <div className={styles.main_section}>
              <div className={styles.headerSection}>
                <h6>Edit Research</h6>
              </div>
              <Stepper
                steps={[{ label: "Basic Info" }, { label: "Advanced" }]}
                activeStep={step}
                activeColor="#1d7eeb"
              />
              <Form onSubmit={handleSubmit}>
                {step === 0 && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Research Name *</Form.Label>
                      <Form.Control
                        name="research_name"
                        value={form.research_name}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Type *</Form.Label>
                      <Form.Select
                        name="type"
                        value={form.type}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Type</option>
                        {RESEARCH_TYPES.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Description *</Form.Label>
                      <Form.Control
                        as="textarea"
                        name="description"
                        value={form.description}
                        onChange={handleChange}
                        required
                        rows={4}
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="switch"
                        label="Is Age Limited?"
                        name="is_age_limited"
                        checked={form.is_age_limited}
                        onChange={handleChange}
                      />
                    </Form.Group>
                    {form.is_age_limited && (
                      <Row>
                        <Col>
                          <Form.Group>
                            <Form.Label>Min Age</Form.Label>
                            <Form.Control
                              type="number"
                              name="min_age"
                              value={form.age_limit.min_age}
                              onChange={handleChange}
                              min={0}
                            />
                          </Form.Group>
                        </Col>
                        <Col>
                          <Form.Group>
                            <Form.Label>Max Age</Form.Label>
                            <Form.Control
                              type="number"
                              name="max_age"
                              value={form.age_limit.max_age}
                              onChange={handleChange}
                              min={0}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    )}
                    <div className={styles.btn_section}>
                      <Button variant="secondary" onClick={() => navigate(-1)}>
                        Cancel
                      </Button>
                      <Button onClick={handleNext}>Next</Button>
                    </div>
                  </>
                )}
                {step === 1 && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Duration (minutes) *</Form.Label>
                      <Form.Control
                        name="duration"
                        type="number"
                        value={form.duration}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Category *</Form.Label>
                      <Form.Select
                        name="category"
                        value={form.category}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Category</option>
                        {categories.map((cat) => (
                          <option key={cat._id} value={cat._id}>
                            {cat.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Location *</Form.Label>
                      <Form.Control
                        name="location"
                        value={form.location}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Price *</Form.Label>
                      <Form.Control
                        name="price"
                        type="number"
                        value={form.price}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Max Applications <small>(Accept applications up to this number)*</small></Form.Label>
                      <Form.Control
                        name="max_applications"
                        type="number"
                        value={form.max_applications}
                        onChange={handleChange}
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="switch"
                        label="Status"
                        name="status"
                        checked={form.status}
                        onChange={handleChange}
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="switch"
                        label="Is Form Applicable?"
                        name="is_form_applicable"
                        checked={form.is_form_applicable}
                        onChange={handleChange}
                      />
                    </Form.Group>
                    {form.is_form_applicable && (
                      <Form.Group className="mb-3">
                        <Form.Label>Form Questionnaire</Form.Label>
                        <Form.Select
                          name="form_questionair"
                          value={form.form_questionair}
                          onChange={handleChange}
                        >
                          <option value="">Select Form</option>
                          {forms.map((f) => (
                            <option key={f._id} value={f._id}>
                              {f.form_name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    )}
                    <div className={styles.btn_section}>
                      <Button variant="secondary" onClick={handleBack}>
                        Back
                      </Button>
                      <Button type="submit" disabled={loading}>
                        {loading ? <Spinner size="sm" /> : "Update"}
                      </Button>
                    </div>
                  </>
                )}
              </Form>
            </div>
          </Col>
          {toggleNotification && <Col md={4} sm={12}>
            <Notification />
          </Col>}
        </Row>
      </div>
    </React.Fragment>
  );
};

export default EditProject;
